import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { api, STATUS_OPTIONS, ADVANCE_MODES, getTodayISO, getCurrentMonth, isAdminUser, fetchCentersFromDB } from "@/lib/api";
import { cn } from "@/lib/utils";
import { UnlockPayrollDialog, LockHistoryDialog } from "./PayrollLockDialogs";
import { AttendanceLockBar } from "@/components/AttendanceLockBar";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { 
  Loader2, 
  Download, 
  Save, 
  RefreshCw, 
  Calendar,
  CalendarDays,
  Wallet,
  Lock,
  Users,
  ArrowRightLeft,
  Trash2
} from "lucide-react";

export default function Attendance() {
  const { session } = useAuth();
  const isMGT = isAdminUser(session);

  // Centers list for MGT/Super Admin who can edit any center's attendance
  const [centersList, setCentersList] = useState([]);
  // iter211: default MGT users to ALL CENTERS so they can pick any centre.
  // Regular managers stay locked to their own centre.
  const [activeCenter, setActiveCenter] = useState(
    isMGT ? "ALL" : (session?.center || ""));

  useEffect(() => {
    if (!isMGT) return;
    let cancelled = false;
    (async () => {
      // iter269 — Attendance MUST include PB-MGT (HQ) because it has
      // active employees. `scope=attendance` returns operational centres
      // PLUS any centre with employees so Management is selectable.
      // Sales / Restaurant dropdowns MUST NOT use this scope.
      try {
        const cs = (await fetchCentersFromDB(session.token, "attendance") || [])
          .filter(c => c.code
                        && c.active !== false
                        && c.is_india_center !== false);
        if (!cancelled && cs.length) { setCentersList(cs); return; }
      } catch { /* fall through to POST fallback */ }
      // Fallback: POST /api/mgt/centers with scope=attendance.
      try {
        const res = await api.post("/mgt/centers", {
          token: session.token,
          scope: "attendance",
        });
        const cs = (res.data?.centers || [])
          .filter(c => c.code
                        && c.active !== false
                        && c.is_india_center !== false);
        if (!cancelled) setCentersList(cs);
      } catch { /* leave empty; UI shows ALL CENTERS anyway */ }
    })();
    return () => { cancelled = true; };
  }, [isMGT, session?.token]);

  // Daily state
  const [date, setDate] = useState(getTodayISO());
  const [dailyRows, setDailyRows] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(false);
  
  // Monthly state
  const [month, setMonth] = useState(getCurrentMonth());
  const [monthlyGrid, setMonthlyGrid] = useState([]);
  const [daysInMonth, setDaysInMonth] = useState(30);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [payrollLocked, setPayrollLocked] = useState(false);
  const [attLock, setAttLock] = useState(null);
  const editBlocked = (payrollLocked && attLock?.status !== "REOPENED_FOR_CORRECTION") || !!attLock?.locked;
  
  // Advances state
  const [advances, setAdvances] = useState([]);
  const [advancesLoading, setAdvancesLoading] = useState(false);

  // Check payroll status
  const checkPayrollStatus = async () => {
    // ALL CENTERS view has no single lock — skip; individual centre
    // switches will re-query.
    if (!activeCenter || activeCenter === "ALL") {
      setPayrollLocked(false);
      return;
    }
    try {
      const res = await api.post("/payroll_status", {
        token: session.token,
        center: activeCenter,
        month: month
      });
      setPayrollLocked(res.data.locked);
    } catch (e) {
      console.error("Payroll status error:", e);
    }
  };

  useEffect(() => {
    checkPayrollStatus();
  }, [month, activeCenter]);

  // iter211 — helper: expand "ALL" into every allowed India centre
  //           excluding PB-MGT.  Regular managers get exactly their
  //           own centre (backend also enforces this).
  const expandCentres = () => {
    if (!isMGT) return [session?.center].filter(Boolean);
    if (activeCenter && activeCenter !== "ALL") return [activeCenter];
    return centersList.map(c => c.code);
  };

  // Load employees for daily attendance
  const loadEmployees = async () => {
    setDailyLoading(true);
    try {
      const targets = expandCentres();
      if (targets.length === 0) {
        toast.error("No centre selected. Choose one from the dropdown.");
        return;
      }
      const merged = [];
      for (const ctr of targets) {
        const res = await api.post("/employees", {
          token: session.token, center: ctr,
        });
        for (const e of (res.data.employees || [])) {
          merged.push({
            name: e.name,
            designation: e.designation || "",
            status: "P",
            notes: "",
            advanceAmount: 0,
            advanceMode: "CASH",
            transfer_tag: e.transfer_tag || "HOME",
            transfer_from: e.transfer_from || "",
            transfer_to: e.transfer_to || "",
            center: ctr,
          });
        }
      }
      setDailyRows(merged);
      toast.success(`Loaded ${merged.length} employees across ${targets.length} centre${targets.length > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load employees");
    } finally {
      setDailyLoading(false);
    }
  };

  // Load saved attendance for date
  const loadSavedAttendance = async () => {
    setDailyLoading(true);
    try {
      const targets = expandCentres();
      if (targets.length === 0) {
        toast.error("No centre selected.");
        return;
      }
      const mergedRows = [];
      const advMap = {};
      for (const ctr of targets) {
        const [attRes, advRes] = await Promise.all([
          api.post("/attendance_by_date", { token: session.token, center: ctr, date }),
          api.post("/advances_by_date", { token: session.token, center: ctr, date }),
        ]);
        (advRes.data.rows || []).forEach(a => {
          advMap[`${ctr}|${a.employeeName}`] = { amount: a.advanceAmount || 0, mode: a.mode || "CASH" };
        });
        for (const r of (attRes.data.rows || [])) {
          mergedRows.push({
            name: r.employeeName,
            designation: r.designation || "",
            status: r.status || "P",
            notes: r.notes || "",
            advanceAmount: advMap[`${ctr}|${r.employeeName}`]?.amount || 0,
            advanceMode: advMap[`${ctr}|${r.employeeName}`]?.mode || "CASH",
            transfer_tag: r.transfer_tag || "HOME",
            transfer_from: r.transfer_info?.from_center || "",
            transfer_to: r.transfer_info?.to_center || "",
            center: ctr,
          });
        }
      }
      if (mergedRows.length === 0) {
        toast.info("No saved attendance. Use Load Employees.");
        return;
      }
      setDailyRows(mergedRows);
      toast.success(`Loaded ${mergedRows.length} records across ${targets.length} centre${targets.length > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load attendance");
    } finally {
      setDailyLoading(false);
    }
  };

  // Save daily attendance
  const saveDailyAttendance = async () => {
    if (editBlocked) {
      toast.error("Attendance is locked for this month — unlock it first");
      return;
    }
    if (dailyRows.length === 0) {
      toast.error("Load employees first");
      return;
    }
    
    setDailyLoading(true);
    try {
      // iter211 — Bucket rows by their own row.center so each employee is
      // saved to their proper centre.  In INDIVIDUAL mode every row
      // carries the same centre; in ALL CENTERS mode rows carry the
      // centre they came from.  Fallback to `activeCenter`.
      const buckets = {};
      for (const r of dailyRows) {
        const ctr = r.center || activeCenter;
        if (!ctr || ctr === "ALL") continue;
        buckets[ctr] = buckets[ctr] || [];
        buckets[ctr].push(r);
      }
      if (Object.keys(buckets).length === 0) {
        toast.error("Rows are not tagged with a centre. Reload employees.");
        return;
      }
      let totalNew = 0, totalUpd = 0, advNew = 0, advUpd = 0;
      for (const [ctr, rows] of Object.entries(buckets)) {
        const attRes = await api.post("/bulk_attendance", {
          token: session.token,
          center: ctr,
          date: date,
          rows: rows.map(r => ({
            employeeName: r.name,
            designation: r.designation,
            status: r.status,
            notes: r.notes,
          })),
        });
        totalNew += attRes.data.inserted || 0;
        totalUpd += attRes.data.updated || 0;
        if (attRes.data.payroll_notice) toast.warning(attRes.data.payroll_notice, { duration: 10000 });

        const advRows = rows.filter(r => r.advanceAmount > 0);
        if (advRows.length > 0) {
          const advRes = await api.post("/bulk_advances", {
            token: session.token,
            center: ctr,
            date: date,
            rows: advRows.map(r => ({
              employeeName: r.name,
              advanceAmount: r.advanceAmount,
              mode: r.advanceMode,
              notes: "",
            })),
          });
          advNew += advRes.data.inserted || 0;
          advUpd += advRes.data.updated || 0;
        }
      }
      const ctrCount = Object.keys(buckets).length;
      toast.success(
        `Saved across ${ctrCount} centre${ctrCount > 1 ? "s" : ""}! ` +
        `Attendance: ${totalNew} new, ${totalUpd} updated. ` +
        `Advances: ${advNew} new, ${advUpd} updated.`);
      loadAdvances();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setDailyLoading(false);
    }
  };

  // Update daily row
  const updateDailyRow = (index, field, value) => {
    setDailyRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Load monthly grid
  const loadMonthlyGrid = async () => {
    setMonthlyLoading(true);
    try {
      const res = await api.post("/attendance_month", {
        token: session.token,
        center: activeCenter,
        month: month
      });
      setMonthlyGrid(res.data.grid || []);
      setDaysInMonth(res.data.daysInMonth || 30);
      toast.success(`Loaded ${res.data.grid?.length || 0} employees`);
      checkPayrollStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load month");
    } finally {
      setMonthlyLoading(false);
    }
  };

  // Update monthly cell
  const updateMonthlyCell = (empIndex, dayIndex, status) => {
    setMonthlyGrid(prev => {
      const updated = [...prev];
      updated[empIndex] = {
        ...updated[empIndex],
        days: updated[empIndex].days.map((d, i) => 
          i === dayIndex ? { ...d, status } : d
        )
      };
      return updated;
    });
  };

  // Save monthly grid
  const saveMonthlyGrid = async () => {
    if (editBlocked) {
      toast.error("Attendance is locked for this month — unlock it first");
      return;
    }
    
    setMonthlyLoading(true);
    try {
      const cells = [];
      monthlyGrid.forEach(emp => {
        emp.days.forEach(d => {
          cells.push({
            employeeName: emp.employeeName,
            day: d.day,
            status: d.status || "",
            notes: ""
          });
        });
      });
      
      const res = await api.post("/bulk_attendance_month", {
        token: session.token,
        center: activeCenter,
        month: month,
        cells
      });
      
      toast.success(`Saved! ${res.data.inserted} new, ${res.data.updated} updated`);
      if (res.data.payroll_notice) toast.warning(res.data.payroll_notice, { duration: 10000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setMonthlyLoading(false);
    }
  };

  // iter217 — Lock payroll — respects the currently selected centre.
  // "PB-MGT" is the user's login role, NOT a scope. To lock every
  // centre in one bulk action the SA has to explicitly pick
  // "ALL CENTERS" from the centre dropdown; then we prompt with the
  // full list and require an extra confirmation before firing off the
  // per-centre lock loop.
  const lockPayroll = async () => {
    const selected = (activeCenter || "").toUpperCase();
    if (!selected) {
      toast.error("Pick a centre from the dropdown first.");
      return;
    }

    // Bulk path
    if (selected === "ALL") {
      const list = centersList.map(c => c.code).filter(c => c !== "PB-MGT");
      const summary = list.join("\n  • ");
      const ok1 = window.confirm(
        `You selected ALL CENTERS.\n\n` +
        `This will lock ${month} attendance separately for every ` +
        `eligible centre:\n\n  • ${summary}\n\nContinue?`);
      if (!ok1) return;
      const typed = window.prompt(
        `Type exactly:  LOCK ${list.length}  to confirm bulk lock.`);
      if ((typed || "").trim().toUpperCase() !== `LOCK ${list.length}`) {
        toast.error("Bulk lock cancelled — confirmation phrase mismatch.");
        return;
      }
      try {
        const { data } = await api.post("/lock_payroll", {
          token: session.token, month, scope: "ALL_CENTERS",
        });
        toast.success(`Locked ${data.locked?.length || 0} centres.`);
        checkPayrollStatus();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to lock payroll");
      }
      return;
    }

    // Specific-centre path
    const ok = window.confirm(
      `Lock ${selected} attendance for ${month}?\n\n` +
      `This will stop attendance edits for ${selected} only. Other ` +
      `centres remain open.`);
    if (!ok) return;
    try {
      await api.post("/lock_payroll", {
        token: session.token, month,
        center: selected, scope: "SPECIFIC",
      });
      toast.success(`${selected} locked for ${month}`);
      checkPayrollStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to lock payroll");
    }
  };

  // Load advances for month
  const loadAdvances = async () => {
    setAdvancesLoading(true);
    try {
      const advMonth = date.slice(0, 7);
      const res = await api.post("/advances_by_month", {
        token: session.token,
        center: activeCenter,
        month: advMonth
      });
      setAdvances(res.data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load advances");
    } finally {
      setAdvancesLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-primary">Attendance Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Daily + Monthly Attendance Management for{" "}
            <span className="font-semibold">
              {activeCenter === "ALL" ? "ALL CENTERS" : (activeCenter || "—")}
            </span>
          </p>
        </div>
        {isMGT && (
          <div className="flex items-center gap-2" data-testid="attendance-center-selector">
            <Label className="text-sm">Center:</Label>
            <Select value={activeCenter} onValueChange={setActiveCenter}>
              <SelectTrigger className="w-56 h-9" data-testid="attendance-center-trigger">
                <SelectValue placeholder="Select center" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL" data-testid="attendance-center-item-ALL">
                  ALL CENTERS
                </SelectItem>
                {centersList.map(c => (
                  <SelectItem key={c.code} value={c.code}
                                data-testid={`attendance-center-item-${c.code}`}>
                    {c.code} — {c.name || c.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <AttendanceLockBar session={session} center={activeCenter} month={month} onChange={setAttLock} />
      {/* Payroll status banner */}
      {payrollLocked && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 flex items-center gap-3 flex-wrap"
              data-testid="payroll-locked-banner">
          <Lock className="w-5 h-5 text-destructive" />
          <div className="flex-1 min-w-[220px]">
            <p className="font-bold text-destructive">
              {activeCenter && activeCenter !== "ALL"
                ? `${activeCenter} · Payroll Locked`
                : "Payroll Locked"}
            </p>
            <p className="text-sm text-destructive/80">
              Attendance for {activeCenter && activeCenter !== "ALL" ? `${activeCenter} · ` : ""}{month} is locked. View only mode.
              {!isMGT && " Contact Admin / Super Admin for correction."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <LockHistoryDialog token={session.token} month={month} />
            {isMGT && (
              <UnlockPayrollDialog
                token={session.token}
                month={month}
                center={activeCenter}
                isSuperAdmin={!!session?.is_super_admin}
                isAdmin={!!session?.is_admin}
                onUnlocked={() => { setPayrollLocked(false); checkPayrollStatus(); }}
              />
            )}
          </div>
        </div>
      )}
      {!payrollLocked && activeCenter !== "ALL" && (
        <div className="flex justify-end">
          <LockHistoryDialog token={session.token} month={month} />
        </div>
      )}

      <Tabs defaultValue="daily" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="daily" className="gap-2">
            <Calendar className="w-4 h-4" />
            Daily
          </TabsTrigger>
          <TabsTrigger value="monthly" className="gap-2">
            <CalendarDays className="w-4 h-4" />
            Monthly
          </TabsTrigger>
          <TabsTrigger value="advances" className="gap-2">
            <Wallet className="w-4 h-4" />
            Advances
          </TabsTrigger>
        </TabsList>

        {/* Daily Tab */}
        <TabsContent value="daily" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Daily Attendance</CardTitle>
              <CardDescription>
                Mark attendance with status and advances for each employee
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Date and actions */}
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-44"
                    data-testid="attendance-date"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={loadEmployees}
                  disabled={dailyLoading}
                  data-testid="load-employees-btn"
                >
                  {dailyLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Users className="w-4 h-4 mr-2" />
                  Load Employees
                </Button>
                <Button
                  variant="outline"
                  onClick={loadSavedAttendance}
                  disabled={dailyLoading}
                  data-testid="load-saved-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Load Saved
                </Button>
                <Button
                  onClick={saveDailyAttendance}
                  disabled={dailyLoading || editBlocked}
                  data-testid="save-attendance-btn"
                >
                  {dailyLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Save className="w-4 h-4 mr-2" />
                  Save All
                </Button>
              </div>

              {/* Attendance table */}
              {dailyRows.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full attendance-table">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3">Employee</th>
                          {activeCenter === "ALL" && (
                            <th className="text-left p-3">Centre</th>
                          )}
                          <th className="text-left p-3">Status</th>
                          <th className="text-left p-3">Advance</th>
                          <th className="text-left p-3">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dailyRows.map((row, idx) => {
                          const isTransferredOut = row.transfer_tag === "TRANSFERRED_OUT";
                          const isTransferredIn = row.transfer_tag === "TRANSFERRED_IN";
                          return (
                          <tr key={idx} className={cn("border-b hover:bg-muted/50", isTransferredOut && "opacity-50 bg-red-50/30")}>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <div>
                                  <p className="font-semibold">{row.name}</p>
                                  <p className="text-xs text-muted-foreground">{row.designation}</p>
                                </div>
                                {isTransferredIn && (
                                  <Badge variant="outline" className="text-[10px] border-blue-400 text-blue-600 bg-blue-50" data-testid={`transfer-in-badge-${idx}`}>
                                    <ArrowRightLeft className="w-3 h-3 mr-1" /> In from {row.transfer_from || "?"}
                                  </Badge>
                                )}
                                {isTransferredOut && (
                                  <Badge variant="outline" className="text-[10px] border-orange-400 text-orange-600 bg-orange-50" data-testid={`transfer-out-badge-${idx}`}>
                                    <ArrowRightLeft className="w-3 h-3 mr-1" /> Out to {row.transfer_to || "?"}
                                  </Badge>
                                )}
                              </div>
                            </td>
                            {activeCenter === "ALL" && (
                              <td className="p-3">
                                <Badge variant="outline" className="text-[10px]"
                                        data-testid={`row-center-${idx}`}>
                                  {row.center || "—"}
                                </Badge>
                              </td>
                            )}
                            <td className="p-3">
                              <div className="flex gap-1 flex-wrap">
                                {STATUS_OPTIONS.map(s => (
                                  <button
                                    key={s.value}
                                    onClick={() => !isTransferredOut && updateDailyRow(idx, "status", s.value)}
                                    disabled={isTransferredOut}
                                    className={cn(
                                      "px-3 py-1.5 rounded-md text-xs font-bold border transition-all",
                                      row.status === s.value 
                                        ? s.color 
                                        : "bg-white border-border hover:border-primary/30",
                                      isTransferredOut && "cursor-not-allowed opacity-50"
                                    )}
                                  >
                                    {s.value}
                                  </button>
                                ))}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  min="0"
                                  value={row.advanceAmount || ""}
                                  onChange={(e) => updateDailyRow(idx, "advanceAmount", parseFloat(e.target.value) || 0)}
                                  placeholder="0"
                                  className="w-24 h-8"
                                />
                                <Select
                                  value={row.advanceMode}
                                  onValueChange={(v) => updateDailyRow(idx, "advanceMode", v)}
                                >
                                  <SelectTrigger className="w-24 h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {ADVANCE_MODES.map(m => (
                                      <SelectItem key={m} value={m}>{m}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </td>
                            <td className="p-3">
                              <Input
                                value={row.notes}
                                onChange={(e) => updateDailyRow(idx, "notes", e.target.value)}
                                placeholder="Notes..."
                                className="h-8"
                                disabled={isTransferredOut}
                              />
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No employees loaded</p>
                  <p className="text-sm">Click "Load Employees" to start</p>
                </div>
              )}

              {/* Status legend */}
              <div className="flex flex-wrap gap-4 pt-4 border-t">
                {STATUS_OPTIONS.map(s => (
                  <div key={s.value} className="flex items-center gap-2">
                    <Badge variant="outline" className={s.color}>{s.value}</Badge>
                    <span className="text-sm text-muted-foreground">{s.label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Monthly Tab */}
        <TabsContent value="monthly" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Monthly Attendance Grid</CardTitle>
              <CardDescription>
                View and edit attendance for the entire month
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Month picker and actions */}
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-2">
                  <Label>Month</Label>
                  <Input
                    type="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    className="w-44"
                    data-testid="month-picker"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={loadMonthlyGrid}
                  disabled={monthlyLoading}
                  data-testid="load-month-btn"
                >
                  {monthlyLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Download className="w-4 h-4 mr-2" />
                  Load Month
                </Button>
                <Button
                  onClick={saveMonthlyGrid}
                  disabled={monthlyLoading || editBlocked}
                  data-testid="save-month-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Month
                </Button>
                {isMGT && (
                  <Button
                    variant="destructive"
                    onClick={lockPayroll}
                    disabled={editBlocked || !activeCenter}
                    data-testid="lock-payroll-btn"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    {activeCenter === "ALL"
                      ? "Lock All Centres"
                      : (activeCenter ? `Lock ${activeCenter}` : "Lock Payroll")}
                  </Button>
                )}
              </div>

              {/* Monthly grid */}
              {monthlyGrid.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto max-h-[600px]">
                    <table className="w-max min-w-full">
                      <thead className="sticky top-0 z-10 bg-muted">
                        <tr>
                          <th className="text-left p-2 font-bold text-xs sticky left-0 bg-muted z-20 min-w-[150px]">
                            Employee
                          </th>
                          {Array.from({ length: daysInMonth }, (_, i) => (
                            <th key={i} className="p-2 text-center text-xs font-bold min-w-[50px]">
                              {i + 1}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyGrid.map((emp, empIdx) => {
                          const isOut = emp.transfer_tag === "TRANSFERRED_OUT";
                          const isIn = emp.transfer_tag === "TRANSFERRED_IN";
                          return (
                          <tr key={empIdx} className={cn("border-t hover:bg-muted/30", isOut && "opacity-50 bg-red-50/20")}>
                            <td className="p-2 sticky left-0 bg-card z-10 border-r">
                              <div className="flex items-center gap-1">
                                <div>
                                  <p className="font-semibold text-sm">{emp.employeeName}</p>
                                  <p className="text-xs text-muted-foreground">{emp.designation}</p>
                                </div>
                                {isIn && (
                                  <Badge variant="outline" className="text-[9px] px-1 border-blue-400 text-blue-600 bg-blue-50 whitespace-nowrap">IN</Badge>
                                )}
                                {isOut && (
                                  <Badge variant="outline" className="text-[9px] px-1 border-orange-400 text-orange-600 bg-orange-50 whitespace-nowrap">OUT</Badge>
                                )}
                              </div>
                            </td>
                            {emp.days.map((day, dayIdx) => (
                              <td key={dayIdx} className="p-1 relative">
                                <select
                                  value={day.status || ""}
                                  onChange={(e) => updateMonthlyCell(empIdx, dayIdx, e.target.value)}
                                  disabled={editBlocked || isOut}
                                  title={day.out_of_window
                                    ? "Saved outside this employee's transfer window at this centre — verify"
                                    : ""}
                                  className={cn(
                                    "w-full h-8 text-xs font-bold text-center border rounded cursor-pointer",
                                    day.status === "P" && "bg-emerald-100 border-emerald-300",
                                    day.status === "A" && "bg-red-100 border-red-300",
                                    day.status === "HD" && "bg-amber-100 border-amber-300",
                                    day.status === "WO" && "bg-slate-100 border-slate-300",
                                    day.status === "L" && "bg-blue-100 border-blue-300",
                                    !day.status && "bg-white border-border",
                                    day.out_of_window && "ring-2 ring-orange-400",
                                    isOut && "cursor-not-allowed opacity-50"
                                  )}
                                  data-testid={day.out_of_window
                                    ? `oow-cell-${emp.employeeName}-${day.day}`
                                    : undefined}
                                >
                                  <option value="">-</option>
                                  <option value="P">P</option>
                                  <option value="A">A</option>
                                  <option value="HD">HD</option>
                                  <option value="WO">WO</option>
                                  <option value="L">L</option>
                                </select>
                                {day.out_of_window && (
                                  <span className="absolute -top-1 -right-1 text-[9px] text-orange-600"
                                          title="Outside transfer window">⚠</span>
                                )}
                              </td>
                            ))}
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No monthly data loaded</p>
                  <p className="text-sm">Select a month and click "Load Month"</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Advances Tab */}
        <TabsContent value="advances" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Advances This Month</CardTitle>
              <CardDescription>
                View all advances given to employees (based on daily date: {date.slice(0, 7)})
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Button
                variant="outline"
                onClick={loadAdvances}
                disabled={advancesLoading}
              >
                {advancesLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <RefreshCw className="w-4 h-4 mr-2" />
                Load Advances
              </Button>

              {advances.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-3 text-xs font-bold">Date</th>
                        <th className="text-left p-3 text-xs font-bold">Employee</th>
                        <th className="text-left p-3 text-xs font-bold">Amount</th>
                        <th className="text-left p-3 text-xs font-bold">Mode</th>
                        <th className="text-right p-3 text-xs font-bold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {advances.map((adv, idx) => (
                        <tr key={idx} className="border-t hover:bg-muted/30">
                          <td className="p-3 text-sm">{adv.date}</td>
                          <td className="p-3 font-semibold">{adv.employeeName}</td>
                          <td className="p-3">
                            <Badge variant="secondary">₹{adv.advanceAmount}</Badge>
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">{adv.mode}</td>
                          <td className="p-3 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-800 hover:bg-red-50"
                              disabled={editBlocked}
                              onClick={async () => {
                                if (!window.confirm(`Delete advance of ₹${adv.advanceAmount} for ${adv.employeeName} on ${adv.date}?`)) return;
                                try {
                                  await api.post("/delete_advance", {
                                    token: session.token,
                                    center: activeCenter,
                                    date: adv.date,
                                    employeeName: adv.employeeName,
                                  });
                                  toast.success("Advance deleted");
                                  loadAdvances();
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || "Failed to delete");
                                }
                              }}
                              data-testid={`delete-advance-${idx}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Wallet className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No advances for this month</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
